import React, { useState, useRef, useEffect } from "react";
import { Button, Modal, Icon } from "semantic-ui-react";
import {
  FILES_BY_API,
  FILE_TYPES,
} from "../../../../../EVisa/constants/apiFiles";
import { uploadPassportPortrait } from "../../../../../../../../../../apis/applicantApi";
import "./UploadPassportPortraitPhoto.scss";

const SuccessUpload = () => {
  return (
    <div
      className="upload-passport-photo-container-container"
      style={{ color: "#0ba300" }}
    >
      <div className="success-container">
        <Icon name="check" size="large" />
      </div>
      <h2>Uploaded Successfully</h2>
    </div>
  );
};

const UploadFilesButton = ({ handleUpload, files, uploading }) => {
  return (
    <div className="upload-passport-photo-upload-button">
      <Button onClick={handleUpload} disabled={uploading || files.length === 0}>
        {uploading ? (
          <>
            <Icon name="spinner" loading />
            Uploading
          </>
        ) : (
          "Upload Files"
        )}
      </Button>
    </div>
  );
};

const UploadProgress = ({ uploadProgress }) => {
  return (
    <div
      className="upload-passport-photo-upload-progress"
      style={{ width: `${uploadProgress}%` }}
    >
      <p>
        <span>Uploading: </span>
        <span>{uploadProgress}%</span>
      </p>
    </div>
  );
};

const SelectedFiles = ({ files, uploading, uploadProgress, handleUpload, removeFile }) => {
  return (
    <div className="upload-passport-photo-selected-files">
      <div className="selected-files-title">
        Selected Files ({files.length})
      </div>
      <div className="upload-passport-photo-selected-files-container">
        {files.map((file, index) => (
          <div
            className="upload-passport-photo-selected-files-item-container"
            key={index}
          >
            <div className="files-item-content">
              <div className="files-item-content-name">
                <span>{file.name.split(".").pop()?.toUpperCase()}</span>
              </div>
              <div
                className="files-item-content-info"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                }}
              >
                <p>{file.name}</p>
                <p>{(file.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            </div>
            <Button
              className="files-item-content-remove-button"
              onClick={() => removeFile(index)}
              disabled={uploading}
            >
              <Icon name="x" size="small" style={{ margin: "0px" }} />
            </Button>
          </div>
        ))}
      </div>
      {
        uploading ? (
          <UploadProgress uploadProgress={uploadProgress} />
        ) : null
      }
      <UploadFilesButton
        uploading={uploading}
        files={files}
        handleUpload={handleUpload}
      />
    </div>
  );
};

const UploadPassportPhoto = ({ id, api }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [open, setOpen] = useState(false);

  const fileInputRef = useRef(null);

  const maxFiles = 1;
  const maxSizeMB = 5;
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  const allowedFileTypes = ["image/png", "image/jpg", "image/jpeg"];

  const onError = (message) => {
    console.error("[ERROR]:", message);
  };

  useEffect(() => {
    setUploading(uploadProgress > 0 && uploadProgress < 100);
  }, [uploadProgress]);

  const onUploadComplete = (data) => console.log("Upload complete:", data);

  const validateFiles = (fileList) => {
    // Check number of files
    if (fileList.length > maxFiles) {
      setError(
        `You can only upload up to ${maxFiles} file${maxFiles > 1 ? "s" : ""}`,
      );
      onError(
        `You can only upload up to ${maxFiles} file${maxFiles > 1 ? "s" : ""}`,
      );
      return false;
    }

    // Check file size
    for (const file of fileList) {
      if (file.size > maxSizeBytes) {
        setError(
          `File "${file.name}" exceeds the maximum size of ${maxSizeMB}MB`,
        );
        onError(
          `File "${file.name}" exceeds the maximum size of ${maxSizeMB}MB`,
        );
        return false;
      }

      // Check file type if allowedFileTypes is provided
      if (allowedFileTypes.length > 0) {
        const fileType = file.type;
        const fileExtension = `.${file.name.split(".").pop()}`;
        const isAllowed = allowedFileTypes.some((type) => {
          if (type.includes("*")) {
            // Handle wildcard types like "image/*"
            return fileType.startsWith(type.split("*")[0]);
          }
          return type === fileType || type === fileExtension;
        });

        if (!isAllowed) {
          setError(`File type of "${file.name}" is not allowed`);
          onError(`File type of "${file.name}" is not allowed`);
          return false;
        }
      }
    }
    return true;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setError(null);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files).slice(0, maxFiles);
      if (validateFiles(droppedFiles)) {
        setFiles(droppedFiles);
      }
    }
  };

  const handleFileChange = (e) => {
    setError(null);
    e.preventDefault();
    e.stopPropagation();

    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files).slice(0, maxFiles);
      if (validateFiles(selectedFiles)) {
        setFiles(selectedFiles);
      }
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploadProgress(0);
    setError(null);
    setSuccess(false);
    setUploading(true);

    let progress = 0;
    const progressInterval = setInterval(() => {
      progress += 5;
      setUploadProgress(progress);
      if (progress >= 90) {
        setUploadProgress(progress);
        clearInterval(progressInterval);
      }
    }, 300);

    try {
      const formData = new FormData();
      const file = files[0];
      const fileType = FILES_BY_API[api]?.passport?.type || FILE_TYPES.FILE;
      formData.append("fileName", "passport");
      formData.append(fileType, file);

      const data = await uploadPassportPortrait(id, api, formData);

      if (data.status !== 200) {
        setError(data.message);
        onError(data.message);
        clearInterval(progressInterval);
        setUploading(false);
        return;
      }

      setSuccess(true);
      onUploadComplete("Completed");
      clearInterval(progressInterval);
      setUploadProgress(100);

      setTimeout(() => {
        resetState();
      }, 1500);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unknown error occurred";
      setError(errorMessage);
      onError(errorMessage);
      clearInterval(progressInterval);
    } finally {
      setUploading(false);
    }
  };

  const resetState = () => {
    setFiles([]);
    setError(null);
    setSuccess(false);
    setUploadProgress(0);
    setUploading(false);
    setIsDragging(false);
    setOpen(false);
  }

  /**
   * Remove a file from the list
   * @param {number} index - The index of the file to remove
   */
  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setError(null);
    setSuccess(false);
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        style={{
          marginLeft: "auto",
        }}
      >
        <span>Upload Applicant's Passport Photo</span>
      </Button>

      <Modal size="tiny" open={open} onClose={() => setOpen(false)}>
        <Modal.Header>Upload Applicant's Passport Photo</Modal.Header>
        <Modal.Content>
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`upload-passport-photo-container ${
              isDragging ? "upload-passport-photo-container--dragging" : ""
            }
            ${error ? "upload-passport-photo-container--error" : ""}
            ${success ? "upload-passport-photo-container--success" : ""}
            `}
          >
            {success ? (
              <SuccessUpload />
            ) : (
              <>
                <div className="upload-passport-photo-container-container">
                  <Icon name="upload" size="large" />
                </div>

                <div className="upload-passport-photo-upload-container">
                  <div className="upload-passport-photo-container-text">
                    <h2>Drag the Applicant's Passport Photo here</h2>
                    <p>or click to browse from your computer</p>
                    <small>Max file size: 5MB</small>
                    <small>
                      {maxFiles > 1 ? `, Max files: ${maxFiles}` : ""}
                    </small>
                  </div>
                  <Button
                    basic
                    id="upload-passport-photo-button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    Select Files
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    multiple={maxFiles > 1}
                    accept={allowedFileTypes.join(",")}
                  />
                </div>

                {files.length > 0 && (
                  <SelectedFiles
                    uploading={uploading}
                    files={files}
                    handleUpload={handleUpload}
                    uploadProgress={uploadProgress}
                    removeFile={removeFile}
                  />
                )}
              </>
            )}
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default UploadPassportPhoto;
